import { QuantityUnit } from '../models/unit.model';
import {
  ExpressionValue,
  Baseline,
  OneOfElements,
  IElementID,
  ICountAndUnit,
  Project,
  IProductElement,
  ElementKind,
  IElement,
  OneOfParentElements,
} from '../models/project.model';
import { isNumeric, multiply, removeNumberFormatting } from './math.helpers';
import {
  flattenElements,
  getBuildingVersionById,
  getElementById,
  getPathToElement,
  isElement,
  isProductElement,
} from './recursive-element.helpers';
import { replaceBuildingVersion, updateElements } from './project.helpers';
import { getId, pick } from './object.helpers';
import { getVariablesInExpression, mathJS } from './mathjs';
import {
  getElementAndQuantityProperties,
  isElementExpressionProperty,
  updateElementPropertiesFromVariablesRecord,
} from './element-property.helpers';
import {
  createEmptyExpression,
  createExpression,
  isExpressionValue,
} from './expression-factory.helpers';
import {
  getExpressionVariablesRecord,
  ExpressionVariables,
  ExpressionVariablesRecord,
  defaultVariables,
} from './expression-variables.helpers';
import { isError, isObject } from 'lodash';
import { findDuplicates, hasDuplicates, isDefined } from './array.helpers';
import { cacheFactory } from './function.helpers';
import { IElementProperty } from '../models/element-property.model';
import { replaceCharAt } from './string.helpers';
import { requiredKind } from './element-kind.helpers';
import { EnvironmentHourData } from './energy/weather.models';
import { getElementsInProposal } from './proposal.helpers';
import { IDataTable } from '@shared/models/data-table.model';
import type { ProposalComputeRecord } from '@shared/models/pipeline/compute.model';
import { concreteDensity } from '@shared/templates/categories/concrete';
import { isSpaceDemandElement } from './energy/energy.helpers';
import { profiler } from './pipeline/pipeline-profiler';
import { guards } from './typeguards.helpers';

export enum ExpressionErrorMessage {
  ForbiddenSymbol = 'Forbidden symbol',
  UndefinedProperty = 'Property not defined',
  UnexpectedEnd = 'Unexpected end of expression',
  NotFinite = 'Not a finite number',
  NotString = 'Expression not a string',
  UnknownError = 'Could not evaluate expression',
  MissingEvaluate = 'Missing evaluate function on expression solver.',
  Circular = 'Circular reference found',
  UnhandledCircular = 'Probable circular reference, break to not create infinite loop',
}

export type ResolvedCountRecord = Record<
  IElementID,
  { count: number; unit: QuantityUnit }
>;

export type ISolveOptions = {
  min?: number;
  max?: number;
};

export const VARIABLE_NAMES_WITH_PERCENTAGES: Record<string, boolean> = {
  fill: true,
  reused_content: true,
  ratio: true,
};

const getPropNamesWithPercentageUnits = (properties: IElementProperty[]) =>
  properties
    .filter((p) => isElementExpressionProperty(p) && p.unit === '%')
    .reduce((acc, { name }) => ({ ...acc, [name]: true }), {});

export const getElementPropertyVariableNamesWithPercentages = (
  element: OneOfElements,
  version?: Baseline,
): Record<string, boolean> => {
  return cacheFactory(
    () => {
      // If element is not added to a version use empty path
      const path =
        (!!version && getPathToElement(version, element, false)) || [];

      const properties = [...path, element]
        .map(getElementAndQuantityProperties)
        .flat();

      return getPropNamesWithPercentageUnits(properties);
    },
    `getElementPropertyVariableNamesWithPercentages[${element.id}]`,
    [element],
  );
};

const EVALUATE_EXPRESSION_CACHE_MAX = 65_536;
const evaluateExpressionCache = new Map<string, number>();

/**
 * Clears {@link evaluateExpression} memoization (Vitest runs reset this via tests/setup).
 */
export const resetEvaluateExpressionCache = (): void => {
  evaluateExpressionCache.clear();
};

/**
 * Main pipeline of calculation. Update all ElementProperties and then all elements
 * Resolve all formulas in a version to update all count values to latest value.
 * @param project
 * @param version
 * @param pipelineComputeRecord When provided (same run as {@link enrichFromComputeRecord}), reuse resolved element property numbers when building the variables record so property expressions are not solved twice.
 * @param scopeId Provide an element further down the hierarchy to update only that element to improve performance
 * @returns
 */
export const updateVersionExpressionValues = (
  project: Project,
  version: Baseline,
  weatherHours: EnvironmentHourData[],
  scopeId: IElementID = version.id,
  dataTables: IDataTable[] = [],
  pipelineComputeRecord?: ProposalComputeRecord,
): Project => {
  profiler.time('updateVersionExpressionValues');
  profiler.count('updateVersionExpressionValues:calls');
  // Everytime version is updated we need to fetch the latest scope
  const getScope = (): OneOfParentElements =>
    getElementById(version, scopeId, true);

  // 1. Get all up-to-date variables available for each element (values only)
  const record = getExpressionVariablesRecord(
    project,
    version,
    weatherHours,
    getScope(),
    dataTables,
    pipelineComputeRecord ? { pipelineComputeRecord } : undefined,
  );

  // 2. Update all properties with the freshly calculated variables
  version = updateElementPropertiesFromVariablesRecord(
    version,
    record,
    getScope(),
  );

  // 3. Update all "count" values in this version.
  version = resolveCountExpressions(version, record, getScope());

  // 4. Apply version changes to project
  const updatedProject = replaceBuildingVersion(project, version);

  // 5. Apply heating demand to project if not running in a certain scope
  const result =
    scopeId === version.id
      ? applyHeatingDemand(updatedProject, version.id, weatherHours, dataTables)
      : updatedProject;
  profiler.timeEnd('updateVersionExpressionValues');
  return result;
};

/**
 * Need to apply heating demand AFTER all element expressions are resolved to get correct values for building envelope etc
 * @param project
 * @param versionId
 * @param weatherHours
 * @returns
 */
const applyHeatingDemand = (
  project: Project,
  versionId: Baseline['id'],
  weatherHours: EnvironmentHourData[],
  dataTables?: IDataTable[],
): Project => {
  profiler.count('applyHeatingDemand:calls');
  // Get the latest updated version (since it updates every loop)
  const getVersion = () => getBuildingVersionById(project, versionId, true);
  const heatingDemandElements: IElement[] = getElementsInProposal(getVersion())
    .filter(isElement)
    .filter(isSpaceDemandElement);
  profiler.countAdd(
    'applyHeatingDemand:elements',
    heatingDemandElements.length,
  );

  for (const element of heatingDemandElements) {
    project = updateVersionExpressionValues(
      project,
      getVersion(),
      weatherHours,
      element.id,
      dataTables,
    );
  }
  return project;
};

const minBoundMessagePrefix = 'Value must be greater than or equal to ';
const maxBoundMessagePrefix = 'Value must be less than or equal to ';

/**
 * {@link solve} rejected a result because it sits outside {@link ISolveOptions} min/max.
 */
export class SolveBoundViolation extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SolveBoundViolation';
  }
}

/**
 * True when {@link message} is a min/max failure from {@link solve}.
 * The UI keeps showing these after a valid override is stored, because the
 * expression itself can still sit outside the bounds.
 * @param message - Solver or formatted expression error text
 */
export const isSolveBoundViolationMessage = (
  message: string | undefined,
): boolean =>
  guards.is.string(message) &&
  (message.startsWith(minBoundMessagePrefix) ||
    message.startsWith(maxBoundMessagePrefix));

/**
 * True when {@link solve} rejected a result for sitting outside {@link ISolveOptions}.
 * @param error - Caught solver failure
 */
const isSolveBoundViolation = (error: unknown): error is SolveBoundViolation =>
  error instanceof SolveBoundViolation;

/**
 * Solve formulas but return an expression even if unsuccessful.
 * A min/max failure keeps {@link expression} unchanged, including its last
 * `resolved`. Replacing it with the raw literal would store a number outside
 * those bounds. Other failures still fall back to a fresh expression from the
 * formula string.
 * @param expression - Count to solve. Its `resolved` is kept when the new result breaks min/max.
 * @param variables - Symbols in scope for the formula
 * @param options - Optional min/max enforced by {@link solve}
 */
export const errorHandledSolve = (
  expression: ExpressionValue,
  variables?: ExpressionVariables,
  options?: ISolveOptions,
): ExpressionValue => {
  profiler.count('errorHandledSolve:calls');
  try {
    const newExp = solve(
      expression.value,
      expression.expression,
      variables,
      options,
    );
    return isEqualExpressionValues(newExp, expression, true)
      ? expression
      : newExp;
  } catch (e) {
    handleExpressionError(e);
    // Do not mint a resolved value outside min/max, and do not clamp to the bound.
    if (isSolveBoundViolation(e)) return expression;
    return createExpression(expression.expression);
  }
};

const evaluateExpression = (
  expression: string,
  variables?: ExpressionVariables,
): number | undefined => {
  profiler.count('evaluateExpression:calls');
  if (!mathJS.evaluate) {
    throw new Error(ExpressionErrorMessage.MissingEvaluate);
  }

  // Empty expression is 0
  if (!expression) {
    return 0;
  }
  // Faster than mathJS.evaluate for numeric literals
  if (isNumeric(expression)) {
    return Number(expression);
  }

  const memoKey = evaluateExpressionMemoKey(expression, variables);
  if (memoKey !== undefined && evaluateExpressionCache.has(memoKey)) {
    return evaluateExpressionCache.get(memoKey);
  }

  // Avoid spreading when no variables (common for literal-only expressions after fast path)
  const scope =
    variables === undefined ? { variables: {} } : { ...variables, variables };

  const result = mathJS.evaluate(expression, scope) as unknown;

  // If the result is a boolean, return 1 if true, 0 if false
  if (typeof result === 'boolean') {
    const n = result ? 1 : 0;
    if (
      memoKey !== undefined &&
      evaluateExpressionCache.size >= EVALUATE_EXPRESSION_CACHE_MAX
    ) {
      evaluateExpressionCache.clear();
    }
    if (memoKey !== undefined) evaluateExpressionCache.set(memoKey, n);
    return n;
  }

  if (typeof result === 'undefined') {
    return undefined;
  }

  if (typeof result === 'number') {
    if (
      memoKey !== undefined &&
      Number.isFinite(result) &&
      evaluateExpressionCache.size >= EVALUATE_EXPRESSION_CACHE_MAX
    ) {
      evaluateExpressionCache.clear();
    }
    if (memoKey !== undefined && Number.isFinite(result)) {
      evaluateExpressionCache.set(memoKey, result);
    }
    return result;
  }

  throw new Error(ExpressionErrorMessage.UnknownError);
};

/**
 * Resolve an expression
 * @param value A number or undefined. This takes precedence over expression if defined.
 * @param expression Math expression like "a * 12 / storeys_count"
 * @param variables A record of available variables: { a: 0, b: 1}
 * @param options Options for min and max values etc
 * @returns A resolved expression containing the resulting numeric value
 */
export const solve = (
  value: number | undefined,
  expression: string = '',
  variables?: ExpressionVariables,
  options: ISolveOptions = {},
): ExpressionValue => {
  //  Allow "==", "==", "!=", "<=" or ">=" but not a single "=" or "==="
  if (
    expression.includes('=') && // Only run regex when expression contains "=" for performance reasons
    /(^|[^!<>=])(=|={3,})([^=]|$)/gi.test(expression)
  ) {
    throw new Error(ExpressionErrorMessage.ForbiddenSymbol);
  }
  if (value === undefined && expression.length === 0) {
    return createEmptyExpression();
  }

  // TODO: refactor this crutch. DEC-1517: fallback for density on concrete PC that is type != cast-in-place causes error

  const adjustedExpression =
    expression === concreteDensity && !variables?.reinforcement_steel
      ? 'densityArray[1]'
      : expression;

  // Numeric literal fast path: skip mathJS.evaluate and scope build
  const expressionResult = evaluateExpression(adjustedExpression, variables);
  const valueResult = value;
  // Empty formula uses 0 internally; min/max must not reject the placeholder when an override wins.
  const shouldValidateExpressionAgainstBounds =
    !guards.is.number(valueResult) || adjustedExpression.trim().length > 0;

  const numberValidation = (value: number) => {
    const { min, max } = options;
    // Throw error for NaN, -Infinity and Infinity
    if (!isFinite(value)) {
      throw new Error(ExpressionErrorMessage.NotFinite);
    }

    // Throw error if min or max is violated
    if (typeof min === 'number' && value < min) {
      throw new SolveBoundViolation(`${minBoundMessagePrefix}${min}`);
    }
    if (typeof max === 'number') {
      if (value > max) {
        throw new SolveBoundViolation(`${maxBoundMessagePrefix}${max}`);
      }

      /* 
      Special case for percentage (should maybe add a percentage flag to options object 
      instead of checking if max is 100) 
      */
      if (max === 100 && value * 100 > max) {
        throw new SolveBoundViolation(`${maxBoundMessagePrefix}${max}%`);
      }
    }
  };

  if (
    guards.is.number(expressionResult) &&
    shouldValidateExpressionAgainstBounds
  ) {
    numberValidation(expressionResult);
  }
  if (typeof valueResult === 'number') numberValidation(valueResult);

  const resolved = valueResult ?? expressionResult;
  if (typeof resolved === 'number') {
    return createExpression({
      value,
      expression: adjustedExpression,
      resolved,
    });
  }
  // Something unexpected happened. Seem to happen with the variable names "i", "e"
  throw new Error(ExpressionErrorMessage.UnknownError);
};

enum NumberError {
  Dot = 'Unexpected part ".',
  Comma = 'Unexpected operator ,',
  Space = 'Unexpected part',
  Parenthesis = 'Parenthesis ) expected',
  Arguments = 'Too many arguments in function',
}

const getFormatError = (error: unknown): NumberError | undefined => {
  if (isError(error) && typeof error.message === 'string') {
    const msg = error.message;
    if (msg.startsWith(NumberError.Comma)) {
      return NumberError.Comma;
    }
    // TODO: handle this error
    // if (msg.startsWith(NumberError.Dot)) {
    //   return NumberError.Dot;
    // }
    if (msg.startsWith(NumberError.Space)) {
      return NumberError.Space;
    }
    if (msg.startsWith(NumberError.Parenthesis)) {
      return NumberError.Parenthesis;
    }
    if (msg.startsWith(NumberError.Arguments)) {
      return NumberError.Arguments;
    }
  }
};

/**
 * Resolve an expression and if user have inputed other number formats than
 * supported by mathjs, it will try to fix it. (1,32 => 1.32, 1 999 => 1999)
 * @param expression Math expression like "a * 12 / storeys_count"
 * @param variables A record of available variables: { a: 0, b: 1}
 * @param options Options for min and max values etc
 * @param breakOnError Pass true to break on error to avoid infinite loops
 * @returns A resolved expression containing the resulting numeric value
 */
export const fixNumberFormatSolve = (
  value: number | undefined,
  expression: string,
  variables?: ExpressionVariables,
  options: ISolveOptions = {},
  breakOnError?: NumberError,
): ExpressionValue => {
  // Short to rerun the function
  const rerun = (exp: string, breakOnError?: NumberError) =>
    fixNumberFormatSolve(value, exp, variables, options, breakOnError);

  try {
    return solve(value, expression, variables, options);
  } catch (error) {
    const errorType = getFormatError(error);

    // Try to fix the error if it possible to handle and has not happened before
    if (isError(error) && errorType && errorType !== breakOnError) {
      const { message } = error;
      const char = 'char' in error ? error.char : undefined;
      const payload =
        isObject(error) && 'data' in error ? error.data : undefined;
      const fn =
        isObject(payload) && 'fn' in payload && typeof payload.fn === 'string'
          ? payload.fn
          : undefined;

      // Turn 1,2 + 2 -> 1.2 + 2
      if (
        typeof char === 'number' &&
        typeof message === 'string' &&
        errorType === NumberError.Comma
      ) {
        const removedFormatting = removeNumberFormatting(expression);

        // If the whole expression is a number it's easier to resolve
        if (isNumeric(removedFormatting)) {
          return rerun(removedFormatting);
        }

        // Replace the comma with a dot and try again
        return rerun(replaceCharAt(expression, char - 1, '.'));
      }

      // Turn "1 999 + 23" -> "1999 + 23"
      if (errorType === NumberError.Space) {
        return rerun(
          expression.replace(/(?<=\d)\s+(?=\d)/g, ''),
          NumberError.Space,
        );
      }

      if (errorType === NumberError.Parenthesis) {
        // Get anything inside a parenthesis
        return rerun(
          expression.replace(
            /(?<=\()[^)]+(?=\))/,
            removeSpaceAndCommaBetweenNumbers,
          ),
          NumberError.Parenthesis,
        );
      }
      if (errorType === NumberError.Arguments && fn) {
        return rerun(
          expression.replace(
            new RegExp(`(?<=${fn}\\()[^)]+(?=\\))`, 'g'),
            removeSpaceAndCommaBetweenNumbers,
          ),
          NumberError.Arguments,
        );
      }
    }

    throw error;
  }
};

const removeSpaceAndCommaBetweenNumbers = (expression: string): string =>
  expression
    .replace(/(?<=\d)\s+(?=\d)/g, '') // Remove spaces between numbers
    .replace(/(?<=\d),(?=\d)/g, '.'); // Replace comma between numbers with dot

/**
 * mathjs `rawArgs` extensions that read scope variables by literal name (not as
 * arguments). Their result depends on scope keys that aren't visible to
 * {@link getVariablesInExpression}, so {@link evaluateExpression} must not memoize calls
 * that touch them — different elements share the same expression text but different
 * implicit scope and would receive a stale cached number otherwise.
 *
 * - {@link getSBEFLifetime}: reads `user_defined.lifetime`, `product_lifetime`, `lifetime` and `building_lifetime` from scope.
 * - {@link getParentValue}: reads `parent` from scope.
 */
const UNSAFE_SCOPE_ACCESSOR_FUNCTIONS = [
  'getSBEFLifetime',
  'getParentValue',
] as const;

const containsUnsafeScopeAccessor = (expression: string): boolean => {
  for (const name of UNSAFE_SCOPE_ACCESSOR_FUNCTIONS) {
    if (expression.includes(name)) return true;
  }
  return false;
};

/**
 * Stable cache key for {@link evaluateExpression} when all referenced symbols are primitives (no object/function scopes — avoids incorrect memo hits).
 */
const evaluateExpressionMemoKey = (
  expression: string,
  variables: ExpressionVariables | undefined,
): string | undefined => {
  if (!expression || isNumeric(expression)) return undefined;
  if (containsUnsafeScopeAccessor(expression)) return undefined;

  const names = getVariablesInExpression(expression);
  if (names.includes('parent')) return undefined;

  if (variables === undefined) return `${expression}\x00\x00`;

  const bag = variables as Record<string, unknown>;
  const chunks: string[] = [expression];
  for (const n of names) {
    const v = bag[n];
    if (v !== null && typeof v === 'object') return undefined;
    if (typeof v === 'function') return undefined;
    chunks.push('\x00', n, '\x01', String(v));
  }
  return chunks.join('');
};

/**
 * Update all count expressions in one or multiple versions
 * @param root Root element to update. Can be a version or a project
 * @param variablesRecord A record of availale variables for each element
 * @param scope Elements to update. Provide an element further down the hierarchy to update only that element and not its parents. Defaults to the root.
 * @returns A modified project
 */
export const resolveCountExpressions = <R extends Baseline | Project>(
  root: R,
  variablesRecord: ExpressionVariablesRecord,
  scope: R | OneOfParentElements = root,
): R => {
  const flattened = flattenElements(scope).filter(isProductElement);
  const withVariables = flattened.filter((el) => variablesRecord[el.id]);
  const updatedElements = withVariables
    .map((element) => resolveCountExpressionInElement(element, variablesRecord))
    .filter(isDefined);

  const changeIds = updatedElements.map((e) => e.id);
  if (updatedElements.length && hasDuplicates(changeIds)) {
    const duplicateIds = findDuplicates(changeIds);
    const duplicateSources = flattened.filter((el) =>
      duplicateIds.includes(el.id),
    );
    const scopeContext =
      'name' in scope && 'id' in scope
        ? `scope="${scope.name}" (id=${scope.id})`
        : `scope.id=${getId(scope)}`;
    console.error(
      '[resolveCountExpressions] Duplicate elements from flattenElements:',
      {
        duplicateIds,
        scopeContext,
        duplicateSourceCount: duplicateSources.length,
        duplicateSources: duplicateSources.map((el) => ({
          id: el.id,
          name: el.name,
          kind: el.kind,
        })),
        flattenedElementIds: flattened.map((e) => e.id),
      },
    );
  }

  return updatedElements.length
    ? updateElements(
        root,
        // Only update count (to not overwrite child elements)
        ...updatedElements.map((e) => pick(e, 'id', 'count')),
      )
    : root;
};

/**
 * Update count expression in Element regardless of type.
 * Return undefined if count is not changed.
 * @param element
 * @param variablesRecord
 * @returns A list of updated properties
 */
const resolveCountExpressionInElement = <T extends IProductElement>(
  element: T,
  variablesRecord: ExpressionVariablesRecord,
): Pick<T, 'kind' | 'id' | 'count' | 'unit'> | undefined => {
  const variables = getVariablesById(variablesRecord, element.id);
  const base = pick(
    requiredKind(element, ElementKind.Product),
    'kind',
    'id',
    'unit',
  );

  const count = getResolvedCountExpression(element, variables);

  return element.count !== count
    ? {
        ...base,
        count,
      }
    : undefined;
};

/**
 * Get resolved expression from product
 * @param countAndUnit
 * @param variables
 * @returns
 */
const getResolvedCountExpression = (
  countAndUnit: ICountAndUnit | undefined,
  variables: ExpressionVariables,
  options?: ISolveOptions,
): ExpressionValue => {
  // Safely handle old projects without crashing.
  return typeof countAndUnit?.count !== 'object'
    ? createExpression(0)
    : errorHandledSolve(countAndUnit.count, variables, options);
};

/**
 * Get an existing expression or create a new one from a number or string
 * @param value
 * @returns
 */
export const getExpression = (
  value: number | ExpressionValue | Partial<ExpressionValue> | string = 0,
): ExpressionValue => {
  // Expression already exist so use that
  if (isExpressionValue(value)) {
    return value;
  }

  return createExpression(value);
};

export const getVariablesById = (
  record: ExpressionVariablesRecord,
  id?: IElementID,
): ExpressionVariables => (id && record[id]) || { ...defaultVariables };

/**
 * Handle expression parsing errors in a unified way
 * returning the error message if it's a handled error
 * @param error
 * @returns If it's a handled error return the error message
 */
export const handleExpressionError = (error: unknown): string => {
  const message =
    error instanceof Error ? (error.message as ExpressionErrorMessage) : '';

  if (message === ExpressionErrorMessage.MissingEvaluate) {
    throw error;
  }

  if (
    message === ExpressionErrorMessage.ForbiddenSymbol ||
    message === ExpressionErrorMessage.NotFinite ||
    message === ExpressionErrorMessage.UnknownError
  ) {
    return message;
  }

  // A variable is missing
  if (message.includes('Unexpected type of argument')) {
    return ExpressionErrorMessage.UndefinedProperty;
  }

  // Will return "Undefined symbol [property-name]"
  if (message.includes('Undefined symbol')) {
    return message;
  }

  if (message.includes('Unexpected end of expression')) {
    return ExpressionErrorMessage.UnexpectedEnd;
  }

  return ExpressionErrorMessage.UnknownError;
};

/**
 * Get the resolved (numeric) value of a the count property of an element
 */
export const getResolvedExpression = (
  element: Pick<ICountAndUnit, 'count'>,
): number => element.count?.resolved || 0;

/**
 * Test if the ExpressionValue is equal to  another ExpressionValue
 * By default it will only compare the expression and not the resolved value
 * @param a
 * @param b
 * @param compareResolvedValues Pass true to also compare resolved values
 * @returns
 */
export const isEqualExpressionValues = (
  a: ExpressionValue | undefined,
  b: ExpressionValue | undefined,
  compareResolvedValues = false,
): boolean => {
  return (
    a?.expression === b?.expression &&
    a?.value === b?.value &&
    (!compareResolvedValues || a?.resolved === b?.resolved)
  );
};

export const formatExpressionErrorMessage = ({ message }: Error): string => {
  // Keep math.js diagnostics attached — the generic label alone hides the real operand/type failure.
  if (message.includes('addScalar') || message.includes('subtractScalar')) {
    return `Invalid use of variable: ${message}`;
  }
  if (message.includes('actual: Object')) {
    return `Expression includes invalid variable: ${message}`;
  }

  const first = message.split(' (char ')[0];
  if (first) return first;

  if (message.includes('Undefined symbol')) {
    const undefinedSymbol = message.slice(message.lastIndexOf(' ') + 1);
    return `"${undefinedSymbol}" doesn't exist`;
  }
  return message;
};

export const divideExpressionValue = (
  expressionValue: ExpressionValue,
  value: number,
): ExpressionValue => {
  if (value === 0) {
    throw new Error('Cannot divide by zero');
  }
  if (value === 1) {
    return expressionValue;
  }
  const { resolved, expression } = expressionValue;

  return createExpression({
    resolved: resolved / value,
    expression: isNumeric(expression)
      ? `${+expression / value}`
      : `(${expression}) / ${value}`,
  });
};

export const multiplyExpressionValues = (
  ...values: (ExpressionValue | number | undefined)[]
): ExpressionValue => {
  const expValues = values
    .filter(isDefined)
    .map((value) => getExpression(value));
  const expression = multiplyExpressions(
    ...expValues.map((value) => value.expression),
  );
  const resolved = multiply(expValues.map((value) => value.resolved));
  return createExpression({ expression, resolved });
};

export const multiplyExpressions = (...expressions: string[]): string => {
  const newExpression = expressions
    .map((expression) => expression.trim())
    .map((expression) =>
      needParenthesesInMultiplication(expression)
        ? `(${expression})`
        : expression,
    )
    .filter((expression) => expression !== '1')
    .join(' * ');

  return mathJS.format(mathJS.simplify(newExpression), { notation: 'fixed' });
};

export const needParenthesesInMultiplication = (
  expression: string,
): boolean => {
  expression = expression.trim();
  if (isNumeric(expression)) {
    return false;
  }
  // Return false if already wrapped in parentheses or is a function call
  if (expression.startsWith('(') && expression.endsWith(')')) {
    return false;
  }

  // Check if expression is a function call like max(1,2)
  if (/^[a-zA-Z]+\([^)]*\)$/.test(expression)) {
    return false;
  }

  return /[+-]/.test(expression);
};
